import Pagination from "@material-ui/lab/Pagination";
import { computed, observable } from "mobx";
import * as React from "react";
import { SearchField } from "../components/ui/SearchField";
import { ROWS_PER_PAGE, SEARCH_DEBOUNCE_MS } from "../config";
import { Option } from "../constants";
import { TableParams } from "../network/API";

export type OrderDirection = "asc" | "desc";

export type TableHeaderOption<T> = Option<T> & { sort?: boolean };

export class TableStore {
    name: string | null = null;
    @observable private _totalCount = 0;

    // Offset is intentionally read only -> fixes TPAPORTAL-1077 which leads to an infinite loop
    // Changes should be done via set page()
    @observable _offset = 0;
    @computed get offset() {
        return this._offset;
    }
    resetOffset() {
        this._offset = 0;
    }

    @observable keyword = "";
    @observable orderBy: string;
    @observable orderDir: OrderDirection;

    // Use this, if for a table you need to track when the initial load has finished
    @observable private _initialized = false;

    rowsPerPage: number;
    defaultOrderBy = "";
    defaultOrderDirection: OrderDirection = "asc";

    constructor(options: { orderBy: string; orderDir: OrderDirection; limit?: number }, name: string) {
        this.name = name;
        this.defaultOrderBy = options.orderBy;
        this.defaultOrderDirection = options.orderDir;
        this.orderBy = options.orderBy;
        this.orderDir = options.orderDir;
        this.rowsPerPage = options.limit ?? ROWS_PER_PAGE;
    }

    onChangeSort = (column: string) => {
        if (column === this.orderBy) {
            this.orderDir = this.orderDir === "asc" ? "desc" : "asc";
        } else {
            this.orderBy = column;
            this.orderDir = "asc";
        }
    };

    @computed get page() {
        return Math.floor(this.offset / this.rowsPerPage) + 1;
    }

    set page(pageNumber: number) {
        const newOffset = (pageNumber - 1) * this.rowsPerPage;
        this._offset = newOffset;
    }

    @computed get totalCount() {
        return this._totalCount;
    }

    set totalCount(total: number) {
        this._totalCount = total;

        // We got our first total count -> table is initialized
        this._initialized = true;
    }

    @computed get isEmptyState() {
        return this.totalCount === 0 && !this.keyword && this._initialized;
    }

    handleSearchChange = (search: string) => {
        this._offset = 0;
        this.keyword = search;
    };

    Pagination = (props: { isMobile: boolean }) => (
        <Pagination
            count={Math.ceil(this.totalCount / this.rowsPerPage)}
            page={this.page}
            onChange={(_, number) => {
                this.page = number;
            }}
            boundaryCount={props.isMobile ? 1 : undefined}
            siblingCount={props.isMobile ? 0 : undefined}
        />
    );

    SearchField = (props: { placeholder: string; maxWidth?: boolean }) => {
        return (
            <SearchField
                searchValue={this.keyword}
                data-id="table_search"
                onChange={(search: string) => this.handleSearchChange(search)}
                placeholder={props.placeholder}
                style={{ flexGrow: 1, maxWidth: props.maxWidth ? 320 : "unset" }}
                debounceMs={SEARCH_DEBOUNCE_MS}
            />
        );
    };

    get tableParams(): TableParams {
        const params: TableParams = {
            offset: this.offset,
            limit: this.rowsPerPage,
            orderBy: this.orderBy,
            orderDir: this.orderDir
        };
        if (this.keyword) {
            params.keyword = this.keyword;
        }

        return params;
    }
}

let tableStore: TableStore | null = null;

export function useTableStore(
    tableName: string,
    options: { orderBy: string; orderDir: OrderDirection; limit?: number }
) {
    if (!tableStore || tableStore.name !== tableName) {
        tableStore = new TableStore(options, tableName);
    }

    return tableStore;
}

export function clearTableStore() {
    tableStore = null;
}
